/*
 *
 * memory.js
 *
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import memoryAPI from "../../api/memoryAPI";

import thunk from "../../helpers/thunk";

/* thunks */

const fetchMemories = thunk(
  "memory/fetchMemories",
  memoryAPI.fetchAll,
  (o) => o.r.data
);

const fetchMemory = thunk(
  "memory/fetchMemory",
  memoryAPI.fetchByID,
  (o) => o.r.data
);

const createMemory = thunk(
  "memory/createMemory",
  memoryAPI.create,
  (o) => o.r.data
);

const progressMemory = thunk(
  "memory/progressMemory",
  memoryAPI.progress,
  (o) => o.r.data
);

const importMemory = thunk(
  "memory/importMemory",
  memoryAPI.import,
  (o) => o.r.data
);

const deleteMemory = thunk("memory/deleteMemory", memoryAPI.delete, (o) => o.d);

const fetchMemoriesByTitle = thunk(
  "memory/fetchMemoriesByTitle",
  memoryAPI.fetchByTitle,
  (o) => ({
    data: o.r.data,
    titleID: o.d.titleID,
  })
);

const fetchMemoriesByProject = thunk(
  "memory/fetchMemoriesByProject",
  memoryAPI.fetchByProject,
  (o) => ({
    data: o.r.data,
    projectID: o.d.projectID,
  })
);

const fetchMemoriesByTask = thunk(
  "memory/fetchMemoriesByTask",
  memoryAPI.fetchByTask,
  (o) => ({
    data: o.r.data,
    taskID: o.d.taskID,
  })
);

const assignMemoryToTitle = thunk(
  "memory/assignMemoryToTitle",
  memoryAPI.assignToTitle,
  (o) => ({
    data: o.r.data,
    titleID: o.d.titleID,
  })
);

const assignMemoryToProject = thunk(
  "memory/assignMemoryToProject",
  memoryAPI.assignToProject,
  (o) => ({
    data: o.r.data,
    projectID: o.d.projectID,
  })
);

const unassignMemoryFromProject = thunk(
  "memory/unassignMemoryFromProject",
  memoryAPI.unassignFromProject,
  (o) => ({
    projectID: o.d.projectID,
    memoryIDs: o.d.memoryIDs,
  })
);

const unassignMemoryFromTitle = thunk(
  "memory/unassignMemoryFromTitle",
  memoryAPI.unassignFromTitle,
  (o) => ({
    titleID: o.d.titleID,
    memoryIDs: o.d.memoryIDs,
  })
);

const updateTitleMemoryPenalties = thunk(
  "memory/updateTitleMemoryPenalties",
  memoryAPI.updatePenaltiesByTitle,
  (o) => ({
    penalty: o.d.penalty,
    memoryIDs: o.d.memoryIDs,
    titleID: o.d.titleID,
  })
);

const updateProjectMemoryPenalties = thunk(
  "memory/updateProjectMemoryPenalties",
  memoryAPI.updatePenaltiesByProject,
  (o) => ({
    penalty: o.d.penalty,
    memoryIDs: o.d.memoryIDs,
    projectID: o.d.projectID,
  })
);

const setProjectMemoryWrites = thunk(
  "memory/setProjectMemoryWrites",
  memoryAPI.setWritesByProject,
  (o) => ({
    write: o.d.write,
    memoryIDs: o.d.memoryIDs,
    projectID: o.d.projectID,
  })
);

const setTitleMemoryWrites = thunk(
  "memory/setTitleMemoryWrites",
  memoryAPI.setWritesByTitle,
  (o) => ({
    write: o.d.write,
    memoryIDs: o.d.memoryIDs,
    titleID: o.d.titleID,
  })
);

const setMemoryName = thunk("memory/setMemoryName", memoryAPI.setName, (o) => ({
  ...o.d,
}));

const saveMemoryModifications = createAsyncThunk(
  "memory/saveMemoryModifications",
  async (data, { rejectWithValue, getState }) => {
    const id = [];
    const source = [];
    const target = [];
    const memory = getState().memory.memories[data.memoryID];

    Object.keys(memory.entries).forEach((ID) => {
      const entry = memory.entries[ID];
      const entryID = entry.id;

      if (entry.modified) {
        id.push(entryID);
        source.push(entry.source);
        target.push(entry.targets);
      }
    });
    return await memoryAPI
      .saveModifications({
        id,
        source,
        target,
      })
      .then((r) => {
        return {
          memoryID: data.memoryID,
        };
      })
      .catch((e) => rejectWithValue());
  }
);

/* slice */
const slice = createSlice({
  name: "memory",
  initialState: {
    memories: {},
    error: null,
    isLoading: false,
  },
  reducers: {
    entrySourceModified: (state, action) => {
      const memoryID = action.payload.memoryID;
      const entryID = action.payload.entryID;
      const sourceText = action.payload.sourceText;
      const entry = state.memories[memoryID].entries[entryID];
      entry.source = sourceText;
      entry.modified = true;
    },
    entryTargetModified: (state, action) => {
      const memoryID = action.payload.memoryID;
      const entryID = action.payload.entryID;
      const targetText = action.payload.targetText;
      const targetLang = action.payload.targetLang;

      const entry = state.memories[memoryID].entries[entryID];
      entry.modified = true;
      const target = entry.targets.find((target) => target.lang == targetLang);
      if (target) {
        target.text = targetText;
      } else {
        state.memories[memoryID].entries[entryID].targets.push({
          text: targetText,
          lang: targetLang,
        });
      }
    },
  },
  extraReducers: {
    [fetchMemories.fulfilled]: (state, action) => {
      const memories = action.payload;
      state.isLoading = true;
      state.error = null;

      memories.forEach((memory) => {
        state.memories[memory.ID] = {
          name: memory.name,
          note: memory.note,
          sourceLang: memory.sourceLang,
        };
      });
    },
    [fetchMemory.fulfilled]: (state, action) => {
      const memory = action.payload;
      const entries = [];
      state.isLoading = true;
      state.error = null;

      Object.keys(memory.entries).forEach((entryID) => {
        const entry = memory.entries[entryID];
        const targets = entry[1];
        const targetsObj = targets.map((target) => ({
          text: target[0],
          lang: target[1],
          modifiedBy: target[2],
          modifiedDate: target[3],
        }));
        const entryObj = {
          id: entryID,
          source: entry[0],
          targets: targetsObj,
        };
        entries.push(entryObj);
      });

      state.memories[memory.ID] = {
        name: memory.name,
        note: memory.note,
        sourceLang: memory.sourceLang,
        entries: entries,
        size: memory.size,
        created: memory.created,
      };
    },
    [createMemory.fulfilled]: (state, action) => {
      const memory = action.payload;
      state.isLoading = true;
      state.error = null;

      state.memories[memory.ID] = {
        name: memory.name,
        note: memory.note,
        sourceLang: memory.sourceLang,
      };
    },

    [progressMemory.fulfilled]: (state, action) => {
      const progress = action.payload.progress;
      state.isLoading = true;
      state.error = null;
      state.progress = progress.progress;
    },

    [fetchMemoriesByTitle.fulfilled]: (state, action) => {
      const titleID = action.payload.titleID;
      const memories = action.payload.data;
      state.isLoading = true;
      state.error = null;

      memories.forEach((memory) => {
        state.memories[memory.ID] = {
          name: memory.name,
          note: memory.note,
          sourceLang: memory.sourceLang,
        };
      });
    },
    [fetchMemoriesByProject.fulfilled]: (state, action) => {
      const memories = action.payload.data;
      state.isLoading = true;
      state.error = null;

      memories.forEach((memory) => {
        state.memories[memory.ID] = {
          name: memory.name,
          note: memory.note,
          sourceLang: memory.sourceLang,
        };
      });
    },
    [fetchMemoriesByTask.fulfilled]: (state, action) => {
      const memories = action.payload.data;
      state.isLoading = true;
      state.error = null;

      memories.forEach((memory) => {
        state.memories[memory.ID] = {
          name: memory.name,
          note: memory.note,
          sourceLang: memory.sourceLang,
        };
      });
    },
    [assignMemoryToTitle.fulfilled]: (state, action) => {
      const memory = action.payload.data;
      state.isLoading = true;
      state.error = null;

      state.memories[memory.ID] = {
        name: memory.name,
        note: memory.note,
        sourceLang: memory.sourceLang,
      };
    },
    [saveMemoryModifications.fulfilled]: (state, action) => {
      const memoryID = action.payload.memoryID;
      state.isLoading = true;
      state.error = null;

      state.memories[memoryID].entries.forEach((entry) => {
        delete entry.modified;
      });
    },
    [setMemoryName.fulfilled]: (state, action) => {
      const memoryID = action.payload.memoryID;
      const memoryName = action.payload.memoryName;
      state.isLoading = true;
      state.error = null;

      state.memories[memoryID].name = memoryName;
    },
    [deleteMemory.fulfilled]: (state, action) => {
      const memoryID = action.payload.memoryID;
      state.isLoading = true;
      state.error = null;

      delete state.memories[memoryID];
    },
  },
});

export {
  fetchMemories,
  createMemory,
  progressMemory,
  fetchMemory,
  fetchMemoriesByTitle,
  fetchMemoriesByTask,
  assignMemoryToTitle,
  assignMemoryToProject,
  fetchMemoriesByProject,
  updateTitleMemoryPenalties,
  updateProjectMemoryPenalties,
  setProjectMemoryWrites,
  setTitleMemoryWrites,
  unassignMemoryFromProject,
  unassignMemoryFromTitle,
  saveMemoryModifications,
  setMemoryName,
  deleteMemory,
  importMemory,
};
export const { entrySourceModified, entryTargetModified } = slice.actions;
export default slice.reducer;
